import React from "react";
import _ from "lodash";
import moment from "moment";
import { EntityDescriptor, EntityTableSimpleProps, FieldDescriptor, ITableActionParamForRun, Utils } from "@crispico/foundation-react";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { ReduxReusableComponents } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { Icon, Menu } from "semantic-ui-react";
import { GanttAssignmentEntities } from "./GanttAssignmentEntityDescriptor";
import { TaskTablePage, TaskTablePageProps, TaskTablePageReducers, TaskTablePageState } from "pages/Task/TaskEntityDescriptor";
import { IAction } from "@crispico/react-timeline-10000/types/components/ContextMenu/IAction";
import { OverrideableElement, TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { ClientCustomQuery } from "@crispico/foundation-react/components/CustomQuery/ClientCustomQuery";
import { GanttUtils } from "pages/gantt/GanttUtils";
import { COLUMN_DEFAULT_WIDTH } from "@crispico/foundation-react/components/ColumnConfig/dataStructures";
import { PopupWithHelpTooltip } from "@crispico/foundation-react/components/semanticUiReactExt";

type TaskEntityTablePageForGanttAssignmentProps = {
    entities: GanttAssignmentEntities
} & TaskTablePageProps;

class TaskEntityTablePageForGanttAssignment extends TaskTablePage<TaskEntityTablePageForGanttAssignmentProps> {

    componentDidMount(): void {
        super.componentDidMount();
        this.updateEntities();
    }

    componentDidUpdate(prevProps: TaskEntityTablePageForGanttAssignmentProps) {
        // super.componentDidUpdate is not needed because this do not have the dashboard tab(see getExtraTabPanes)
        if (!prevProps || !_.isEqual(prevProps.entities, this.props.entities)) {
            this.updateEntities();
        }
    }

    private prepareData(entities: GanttAssignmentEntities): { columns: string[] | undefined, rows: string[] } {
        if (!entities || !entities["Task"]) {
            return { rows: [], columns: undefined };
        }
        entities = _.cloneDeep(entities);

        // compute columns started from task for populated fields
        const addColumn = (columns: Set<string>, entity: any, ed: EntityDescriptor, chain?: string) => {
            if (!entity) {
                return;
            }
            Object.keys(entity).forEach(columnName => {
                let chainName = chain ? (chain + "." + columnName) : columnName;
                let fdc: FieldDescriptor[] = [];
                try {
                    if (chainName.startsWith("taskGroup.rotationFlight.")) {
                        fdc = ed.getFieldDescriptorChain(chainName.substring("taskGroup.rotationFlight.".length));
                    } else {
                        fdc = ed.getFieldDescriptorChain(chainName);
                    }
                } catch { }
                const fd = fdc[fdc.length - 1];
                if (!fd) {
                    return;
                }

                if (ed.name === "HumanResourceSchedule" && chain === "") {
                    chainName = "mission.humanResource.humanResourceSchedule" + "." + columnName;
                }

                if (chainName === "taskGroup.rotationFlight") {
                    addColumn(columns, entities["Flight"][entity[columnName].id], entityDescriptors["Flight"], "taskGroup.rotationFlight");
                } else if (chainName === "taskGroup.rotationFlight.rotationFlight") {
                    return;
                }

                if (!fd.typeIsEntity()) {
                    columns.add(chainName);
                } else if (entities[fd.type]) {
                    const id = entity[columnName]?.id;
                    addColumn(columns, entities[fd.type][id], ed, chainName);

                    const hrs = entities["HumanResourceSchedule"];
                    if (fd.type === "HumanResource" && hrs) {
                        Object.keys(hrs).forEach(key => {
                            if (hrs[Number(key)].humanResource?.id === id) {
                                delete hrs[Number(key)].humanResource;
                                addColumn(columns, hrs[Number(key)], entityDescriptors["HumanResourceSchedule"], "");
                            }
                        });
                    }
                }
            });
        }
        const columns = new Set<string>();

        Object.keys(entities["Task"]).forEach(key => {
            addColumn(columns, entities["Task"][Number(key)], entityDescriptors["Task"]);
        });

        const rows = Object.keys(entities["Task"]).map(key => GanttUtils.toEntityUid("Task", entities["Task"][Number(key)].id));
        return { columns: Array.from(columns), rows };
    }

    protected updateEntities() {
        const { columns, rows } = this.prepareData(this.props.entities);
        this.entityTableSimpleRef.currentNotNull.setEntities(rows);
        const defaultCC = entityDescriptors["Task"].getDefaultColumnConfig();
        if (columns && defaultCC) {
            defaultCC.configObject!.columns = columns?.map(column => {
                return { name: column, width: COLUMN_DEFAULT_WIDTH }
            });
            // mark as dirty so it is not replaced with the one saved in session storage
            defaultCC.dirty = true;
            defaultCC.id = -2;
            this.columnConfigDropdownRef.current?.props.dispatchers.initColumnConfig(defaultCC);
        }

        this.props.r.setInReduxState({ totalCount: rows.length, loaded: rows.length });
    }

    protected getRowsForExport() {
        const columns = this.columnConfigDropdownRef.currentNotNull.props.dispatchers.getState().columnConfig?.configObject!.columns || [];
        const rows: any[] = [];
        const header: string[] = [];

        const fds = columns.map((column: any) => {
            try {
                let columnLabel = '';
                const fdc = entityDescriptors["Task"].getFieldDescriptorChain(column.name);
                if (fdc.length > 1) {
                    for (const fd of fdc) {
                        const l = fd.getLabel();
                        columnLabel += l + '.';
                    }
                    columnLabel = columnLabel.substring(0, columnLabel.length - 1);
                } else {
                    columnLabel = fdc[0].getLabel();
                }
                header.push(columnLabel);
                return fdc[fdc.length - 1];
            } catch { return undefined; }
        })

        rows.push(header);

        this.entityTableSimpleRef.currentNotNull.getEntities().forEach(entity => {
            rows.push(columns?.map((column: any, index: number) => {
                let value = Utils.navigate(GanttUtils.findByUid(entity, this.props.entities), column.name, false, ".");
                if (!value) {
                    return "";
                } else if (fds[index]?.type === "date") {
                    return moment(value).format(fds![index]!.format);
                } else if (typeof value === "object") {
                    return JSON.stringify(value);
                }
                return value;
            }));
        })

        return rows;
    }

    protected async resetStateBeforeRefresh() {
    }

    protected canRenderEditOption(_: EntityDescriptor, __?: FieldDescriptor) {
        return false;
    }

    protected getTableSimpleProps(): Partial<EntityTableSimpleProps> | undefined {
        // this will disable sort for columns, because the sort is not on client side.
        return { customQueryBarRef: undefined };
    }

    protected refreshIfNeeded() {
        // this is needed because this table don't have the CQ and the data isn't loaded from DB
        // on change CC need to refresh the table
        this.forceUpdate();
    }

    protected needToInitializeCC(): boolean {
        return false;
    }

    getEntityAt(index: number) {
        const entityUid = this.entityTableSimpleRef.currentNotNull.getEntities()[index];
        return entityUid && GanttUtils.findByUid(entityUid, this.props.entities);
    }

    async loadEntities(_: { startIndex: number, pageSize: number, countMode?: boolean }) {
        return;
    }

    async loadAttachedDashboards() {
    }

    protected onDoubleClickItem(_: any) {
    }


    protected getExtraTabPanes(): (TabRouterPane | null)[] {
        return [];
    }

    protected preRenderButtons(_: {}): Array<OverrideableElement> {
        return [{
            element: <PopupWithHelpTooltip key="info" tooltip={_msg("GanttAssignmentEntityTablePage.info.label")} />
        }];
    }

    protected provideActionsForRow(_: ITableActionParamForRun): IAction[] {
        return [];
    }

    protected renderCustomQueryBar(_?: (cq: ClientCustomQuery) => void) {
        return <></>;
    }

    protected renderRefreshButton() {
        return <></>;
    }

    protected renderCompactBarMenuModal() {
        return <Menu vertical className="wh100" borderless>
            <Menu.Item onClick={() => Utils.exportToCsv("GanttAssignment", this.getRowsForExport())}>
                {_msg("dto_crud.export")}
                <Icon name='download' />
            </Menu.Item>
        </Menu>;
    }
}

export const TaskEntityTablePageForGanttAssignmentRRC = ReduxReusableComponents.connectRRC(TaskTablePageState, TaskTablePageReducers, TaskEntityTablePageForGanttAssignment);
